/* DeliveryStatusToast — after a quote/invoice email send, poll the backend
   for the actual Resend delivery status and show it as a live sonner toast.
   This is what stops the "I clicked send but nothing shows up in the inbox"
   confusion — the toast will flip to delivered / bounced / suppressed so
   the admin knows exactly what happened. */

import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Kick off a background poll for delivery status.
 * @param {string} recordId - The email_record_id returned by the send endpoint.
 * @param {string} recipientLabel - Human-readable recipient for the toast.
 * @param {object} opts - { intervalMs, maxAttempts }
 */
export function trackEmailDelivery(recordId, recipientLabel, opts = {}) {
  if (!recordId) return;
  const intervalMs = opts.intervalMs || 3000;
  const maxAttempts = opts.maxAttempts || 15; // 15 * 3s = 45s
  const toastId = `deliver-${recordId}`;

  toast.loading(`Sending to ${recipientLabel}…`, {
    id: toastId,
    description: "Checking delivery in real time",
  });

  let attempts = 0;
  const timer = setInterval(async () => {
    attempts += 1;
    try {
      const res = await axios.get(`${API_URL}/emails/records/${recordId}/live-status`);
      const status = (res.data?.status || "").toLowerCase();
      const detail = res.data?.status_detail || "";

      if (status === "delivered") {
        clearInterval(timer);
        toast.success(`Delivered to ${recipientLabel}`, {
          id: toastId,
          description: "Resend confirmed the inbox received it.",
        });
        return;
      }
      if (status === "opened" || status === "clicked") {
        clearInterval(timer);
        toast.success(`${recipientLabel} opened your email`, {
          id: toastId,
          description: status === "clicked" ? "They also clicked a link." : undefined,
        });
        return;
      }
      if (status === "bounced" || status === "complained" || status === "suppressed") {
        clearInterval(timer);
        toast.error(
          status === "suppressed"
            ? `Blocked — ${recipientLabel} is on the suppression list`
            : status === "complained"
              ? `${recipientLabel} marked it as spam`
              : `Bounced — ${recipientLabel} could not receive`,
          {
            id: toastId,
            description: detail || "The address previously bounced. Ask Resend support (or your admin) to remove it from the suppression list, then try again.",
            duration: 15000,
          },
        );
        return;
      }
      if (attempts >= maxAttempts) {
        clearInterval(timer);
        toast.info(`Sent to ${recipientLabel} — still waiting on delivery confirmation`, {
          id: toastId,
          description: "Resend has accepted the message. Check the Email Log tab for the final status.",
          duration: 8000,
        });
      }
    } catch (e) {
      if (attempts >= maxAttempts) {
        clearInterval(timer);
        toast.dismiss(toastId);
      }
    }
  }, intervalMs);
}
